import { useState, useCallback, useEffect, useRef } from "react";
import { rewriteUrl } from "~/utils/rewrite-news-urls";

/**
 * Hook to copy markdown content to clipboard with HTML and plain text formats.
 *
 * @param newsProvider - The news provider for URL rewriting (default: "mtnews")
 * @returns Object with `copied` state and `copyToClipboard` function
 *
 * @example
 * ```tsx
 * const { copied, copyToClipboard } = useCopyMarkdown("mtnews");
 *
 * <Button onClick={() => copyToClipboard(markdownContent)}>
 *   {copied ? "Copied!" : "Copy"}
 * </Button>
 * ```
 */
export function useCopyMarkdown(newsProvider: string = "mtnews") {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback(
    (content: string) => {
      // Convert markdown links to HTML links with rewritten URLs
      const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const htmlContent = content.replace(
        markdownLinkRegex,
        (_, text, url) =>
          `<a href="${rewriteUrl(url, false, newsProvider)}">${text}</a>`
      );

      // Replace newlines with <br> tags for HTML format
      const htmlWithBreaks = htmlContent.replace(/\n/g, "<br>");

      // Create temporary container to process HTML
      const tempContainer = document.createElement("div");
      tempContainer.textContent = htmlWithBreaks; // Use textContent to avoid XSS

      // Create blobs for both formats
      const htmlBlob = new Blob([htmlWithBreaks], {
        type: "text/html",
      });
      const textBlob = new Blob([content], {
        type: "text/plain",
      });

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Try modern Clipboard API first (supports HTML + text)
      try {
        navigator.clipboard
          .write([
            new ClipboardItem({
              "text/html": htmlBlob,
              "text/plain": textBlob,
            }),
          ])
          .then(() => {
            setCopied(true);
            timeoutRef.current = setTimeout(() => setCopied(false), 2000);
          });
      } catch {
        // Fallback to plain text only
        navigator.clipboard.writeText(content).then(() => {
          setCopied(true);
          timeoutRef.current = setTimeout(() => setCopied(false), 2000);
        });
      }
    },
    [newsProvider]
  );

  return { copied, copyToClipboard };
}
